import OpenApiConfiguration from 'api/OpenApiConfiguration';
import { useUserDetails } from 'auth/mutations/userHooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useInfiniteQuery } from 'react-query';
import { AccountApi } from 'swagger/apis/account-api';
import { PER_PAGE, TabNames } from '../constants';
import { activeConversationIdAtom, activeTabNameAtom, enabledTabsAtom } from '../store';
import useMessagesFilterSelectedValues from './useMessagesFilterSelectedValues';

const accountApi = new AccountApi(OpenApiConfiguration);

const getHasConversation = (pages) => !(pages.length === 0 || pages[0]?.data.length === 0);

export const filterData =
  ({ isAllFilterSelected, isArchiveFilterSelected, isReadMessagesFilterSelected }) =>
  (data) => {
    if (isAllFilterSelected || isArchiveFilterSelected) {
      return data;
    }
    const newPages = data.pages.map((page) => ({
      ...page,
      data: page.data.filter((conversation) => conversation.read === isReadMessagesFilterSelected),
    }));
    return { ...data, pages: newPages };
  };

const getNextPageParam = (lastPage, allPages) => {
  const compiled = (allPages || []).reduce((prev, current) => [...prev, ...current.data], []);
  return compiled.length !== parseInt(lastPage.headers['x-total'], 10) ? allPages.length + 1 : undefined;
};

export const useInfiniteConversationsByType = (type: TabNames, enabled: boolean) => {
  const activeConversationId = useAtomValue(activeConversationIdAtom);
  const setActiveTabName = useSetAtom(activeTabNameAtom);
  const filterSelectedValues = useMessagesFilterSelectedValues();
  const { isAllFilterSelected, isArchiveFilterSelected, isReadMessagesFilterSelected, isUnreadMessagesFilterSelected } =
    filterSelectedValues;
  const [enabledTabs, setEnabledTabs] = useAtom(enabledTabsAtom);
  const { userId } = useUserDetails();

  const getCategory = () => {
    if (isAllFilterSelected) return 'inbox';
    if (isArchiveFilterSelected) return 'archived';
    if (isUnreadMessagesFilterSelected) return 'unread';
    // TODO:  Add `sent` category when is added to the backend
    if (isReadMessagesFilterSelected) return 'inbox';
    return 'inbox';
  };

  const category = getCategory();

  const { data: { pages = [] } = {}, ...other } = useInfiniteQuery(
    ['conversationsTab', type, userId, category],
    ({ pageParam = 1 }) => accountApi.domesticV2MeInboxGet(category, type, undefined, PER_PAGE, pageParam),
    {
      getNextPageParam,
      onSuccess: (data) => {
        // set active tab to corresponding active conversation's category
        // only when loaded first
        if (!other.isFetchedAfterMount && activeConversationId) {
          const conversation = data.pages?.[0]?.data.find(
            ({ conversation_id }) => conversation_id === activeConversationId
          );
          if (conversation) {
            setActiveTabName(conversation.my_user_type_in_conversation);
          }
        }
        // set if tab is enabled only on first load and only if is not enabled yet
        if (!other.isFetchedAfterMount && isAllFilterSelected && type !== TabNames.renter) {
          const hasConversation = getHasConversation(data.pages);
          if (hasConversation && !enabledTabs[type]) {
            setEnabledTabs({ ...enabledTabs, [type]: true });
          }
        }
      },
      select: filterData(filterSelectedValues),
      staleTime: 60000,
      enabled: !!userId && enabled,
    }
  );

  return { pages, hasConversation: getHasConversation(pages), ...other };
};
